import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  cancelClientPayment,
  confirmClientPayment,
  fetchClientPayments,
  requestClientPayment,
} from "../services/clientPaymentsService";
import type { ClientPayment, NewClientPayment } from "../types/clients.types";

export type ClientPaymentsStatus = "loading" | "ready" | "failed";

export interface ClientPaymentsState {
  /** This client's payment requests, newest first. */
  payments: ClientPayment[];
  status: ClientPaymentsStatus;
  /** The server's reason for a failed load, where it gave one. */
  error?: string;
  /** What the failed state's retry calls. */
  reload: () => void;
  /**
   * Raises a request against this client and hands back the row the backend
   * stored — which is also written straight to the top of the list, since the
   * response is a whole row and a refetch would only ask for what is in hand.
   */
  request: (input: NewClientPayment) => Promise<ClientPayment>;
  /**
   * Marks a raised payment as received on the day it came in, as an ISO date.
   * Nothing reports the money in, so that day is the dietitian's to state.
   */
  markPaid: (payment: ClientPayment, receivedOn: string) => Promise<void>;
  /** Calls a raised payment off, leaving it in the history as cancelled. */
  cancel: (paymentId: string) => Promise<void>;
}

/** Handed out before the first load lands, and the same array every time: a
 *  fresh one per render would rebuild every memo reading it. */
const EMPTY: ClientPayment[] = [];

export const clientPaymentsQueryKey = (clientId: string) =>
  ["clients", "payments", clientId] as const;

/**
 * One client's payment history, loaded when their Payments tab opens.
 *
 * Backed by TanStack Query, cached per client: reopening a tab already
 * visited shows that history immediately while a fresh read runs behind it,
 * so a request raised on another device still catches up within moments
 * instead of making every open wait on the network.
 *
 * Every write here is a call: raising a request, marking one received and
 * calling one off all go to the server and land back in the list as the row
 * the server answered with. None of them refetches the history — each response
 * carries the whole row, so a second request would ask for what is in hand.
 * Each is written through `setQueryData` rather than a plain state setter, so
 * the cache for this client carries the write too — a remount right after
 * does not show the pre-write row while the background revalidate catches up.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useClientPayments(clientId: string): ClientPaymentsState {
  const getToken = useTokenGetter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = clientPaymentsQueryKey(clientId);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchClientPayments(getToken, clientId, signal),
  });

  const error =
    query.isError && query.error instanceof ApiError
      ? query.error.message
      : query.isError
      ? "We could not reach the server. Check your connection and try again."
      : undefined;

  // Only fires when there is nothing cached to show in its place — see
  // `useDashboard` for why that is what `isError` means here.
  useEffect(() => {
    if (!query.isError) return;
    console.error("client payments failed to load:", query.error);
    toast.error("Could not load this client’s payments", error ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  const request = useCallback(
    async (input: NewClientPayment) => {
      const raised = await requestClientPayment(getToken, clientId, input);
      // At the top: it is the newest request, and the list is newest first.
      queryClient.setQueryData<ClientPayment[]>(
        queryKey,
        (current: ClientPayment[] | undefined) => [raised, ...(current ?? EMPTY)]
      );
      return raised;
    },
    [getToken, clientId, queryClient, queryKey]
  );

  /**
   * Puts a settled row back in the list, in the place the one it replaces
   * held. Both writes below go through this: each answers with the whole row
   * as the backend now has it, so what lands on screen is what was stored
   * rather than what the click assumed.
   */
  const replace = useCallback(
    (settled: ClientPayment) =>
      queryClient.setQueryData<ClientPayment[]>(
        queryKey,
        (current: ClientPayment[] | undefined) =>
          (current ?? EMPTY).map((payment: ClientPayment) =>
            payment.id === settled.id ? settled : payment
          )
      ),
    [queryClient, queryKey]
  );

  const markPaid = useCallback(
    async (payment: ClientPayment, receivedOn: string) => {
      replace(await confirmClientPayment(getToken, payment, receivedOn));
    },
    [getToken, replace]
  );

  const cancel = useCallback(
    async (paymentId: string) => {
      replace(await cancelClientPayment(getToken, paymentId));
    },
    [getToken, replace]
  );

  return {
    payments: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error,
    reload: () => {
      void query.refetch();
    },
    request,
    markPaid,
    cancel,
  };
}
